import { PlusIcon } from "@heroicons/react/24/solid";
import { IconUploadDragger } from "assets/img/icons";
import { MEDIA_TYPE, PLACEHOLDER_IN_BASE64, SUPPORTED_FILE, UPLOADER_TYPE } from "constants/common";
import THEME from "constants/themes";
import DynamicWidgetContext from "contexts/DynamicWidget";
import { isEmpty } from "lodash";
import { WidgetFieldTypes } from "models/DynamicWidget";
import { ReactNode, useContext } from "react";
import Dropzone from "react-dropzone";
import { useTranslation } from "react-i18next";
import utils from "services/utils";
import { showErrorMsg, thumbnailGenerator } from "services/widgetBuilder";
import {
	StyledIcon,
	StyledOutlineButton,
	StyledText,
	StyledWrapper,
} from "styled-components/Common/CommonStyled";
import { StyledUploadWrapper } from "styled-components/DynamicWidget/DynamicWidgetStyled";
import SocialUpload from "./SocialUpload";
import WidgetContainerContext from "contexts/WidgetContainer";

type UploadSectionProps = {
	uploadFieldDetails: WidgetFieldTypes;
};

const UploadSection = (props: UploadSectionProps) => {
	const { uploadFieldDetails } = props;
	const { t } = useTranslation();
	const { localFileList, setLocalFileList, widgetDetails, setActiveUploadType } =
		useContext(DynamicWidgetContext);
	const { globalProps } = useContext(WidgetContainerContext);
	const { setIsShowLoading } = globalProps;

	const { metadata: { colors = {} } = {}, widgetSettingsDTO } = widgetDetails || {};
	const { maxFileSize } = widgetSettingsDTO || {};
	const { bgFileDropZone, colorUploadIcon } = colors || {};
	const { helperText = "", showSupportFileTypeAndLimit = false } = uploadFieldDetails || {};

	const validateUploadedFilesCustom = async (files: File[]) => {
		const { errorMsg, isValidFiles } = await utils.validateUploadedFiles(
			files,
			"en",
			widgetSettingsDTO,
			true,
			localFileList,
		);

		if (!isValidFiles) {
			showErrorMsg(errorMsg);
			return false;
		}

		return true;
	};

	const onDrop = async (files: File[]) => {
		const fileUploads = await utils.convertFileUploads(files, setIsShowLoading);

		// Validate the number of photos/videos
		await validateUploadedFilesCustom(fileUploads)
			.then(async (result) => {
				const listTemp = await Promise.all(
					fileUploads.map(async (file) => {
						// Validate each file
						const error = await utils.validateFileLimitation({
							file,
							widgetInfo: widgetDetails,
							t,
						});
						return { file, ...(isEmpty(error) ? {} : { error }) };
					}),
				);

				setActiveUploadType(UPLOADER_TYPE.LOCAL_UPLOAD);
				let updatedList = result ? [...localFileList, ...listTemp] : localFileList;

				updatedList = await Promise.all(
					updatedList.map(async (item) => {
						let thumbnail = PLACEHOLDER_IN_BASE64.IMAGE;
						const urlCreator = window.URL || window.webkitURL;

						if (item.file) {
							const objectURL = urlCreator.createObjectURL(item.file);
							thumbnail = item.file.type.includes(MEDIA_TYPE.VIDEO)
								? await thumbnailGenerator(objectURL)
								: objectURL;
						}

						return { ...item, thumbnail };
					}),
				);
				setLocalFileList(updatedList);
			})
			.catch((error: any) => {
				console.log("Error", error);
			});
	};

	const renderDropzone = (children: ReactNode) => {
		return (
			<Dropzone onDrop={(files) => onDrop(files)} accept={SUPPORTED_FILE.ACCEPTED}>
				{({ getRootProps, getInputProps }) => (
					<StyledWrapper
						id="dropzone-upload"
						display="flex"
						alignItems="center"
						justifyContent="center"
						width="100%"
						height="100%"
						cursor="pointer"
						{...getRootProps()}>
						<input {...getInputProps()} />
						{children}
					</StyledWrapper>
				)}
			</Dropzone>
		);
	};

	return (
		<StyledWrapper width="100%">
			{!localFileList.length ? (
				<>
					<StyledUploadWrapper background={bgFileDropZone}>
						{renderDropzone(
							<StyledWrapper>
								<StyledIcon colorPath={colorUploadIcon}>
									<IconUploadDragger />
								</StyledIcon>
								{helperText && <StyledText color={THEME.colors.gray1}>{helperText}</StyledText>}
							</StyledWrapper>,
						)}
					</StyledUploadWrapper>

					{showSupportFileTypeAndLimit && (
						<StyledText
							align="center"
							fontSize="12px"
							color={THEME.colors.gray1}
							margin="8px 0 0 0"
							dangerouslySetInnerHTML={{
								__html: t("widget_builder.supported_file_types", {
									maxFileSize,
								}),
							}}
						/>
					)}

					<SocialUpload uploadFieldDetails={uploadFieldDetails} />
				</>
			) : (
				renderDropzone(
					<StyledOutlineButton
						radius="12px"
						margin="24px 0 0 0"
						width="100%"
						maxWidth="400px"
						icon={
							<StyledIcon size={20}>
								<PlusIcon />
							</StyledIcon>
						}>
						{t("button.add_more")}
					</StyledOutlineButton>,
				)
			)}
		</StyledWrapper>
	);
};

export default UploadSection;
